import React, { useEffect, useRef, useState, useCallback } from "react";
import { UploadCloud, Trash2, Copy, Loader2 } from "lucide-react";
import { api, resolveImg } from "@/lib/api";
import { toast } from "sonner";
import { AdminShell } from "@/components/admin/AdminShell";

export default function MediaLibrary() {
  const [items, setItems] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [drag, setDrag] = useState(false);
  const inputRef = useRef(null);

  const load = useCallback(() => api.get("/admin/media").then((r) => setItems(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const upload = async (files) => {
    if (!files || !files.length) return;
    setUploading(true);
    let ok = 0;
    for (const file of files) {
      const form = new FormData();
      form.append("file", file);
      try {
        await api.post("/admin/media/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
        ok += 1;
      } catch (e) {
        toast.error(`${file.name}: ${e?.response?.data?.detail || "Upload failed"}`);
      }
    }
    if (ok) toast.success(`Uploaded ${ok} file${ok > 1 ? "s" : ""}`);
    load();
    setUploading(false);
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this file from the library?")) return;
    await api.delete(`/admin/media/${id}`);
    load();
  };

  const copy = (url) => {
    navigator.clipboard.writeText(resolveImg(url));
    toast.success("URL copied");
  };

  return (
    <AdminShell title="Media Library" subtitle="Upload once, reuse anywhere">
      <div
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => { e.preventDefault(); setDrag(false); upload(e.dataTransfer.files); }}
        onClick={() => inputRef.current?.click()}
        className={`cursor-pointer rounded-md border-2 border-dashed mb-8 h-40 flex flex-col items-center justify-center gap-2 transition-colors ${drag ? "border-brass bg-brass/5" : "border-espresso/20 hover:border-espresso/40 bg-ivory"}`}
        data-testid="media-dropzone"
      >
        {uploading ? <Loader2 className="animate-spin text-taupe" /> : <UploadCloud size={30} className="text-taupe" strokeWidth={1.4} />}
        <span className="text-sm text-taupe">{uploading ? "Uploading…" : "Drag & drop images or video, or click to browse"}</span>
        <input ref={inputRef} type="file" multiple accept="image/*,video/*" className="hidden" onChange={(e) => upload(e.target.files)} data-testid="media-input" />
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
        {items.map((m) => (
          <div key={m.id} className="bg-ivory border border-espresso/10 rounded-md overflow-hidden group" data-testid={`media-item-${m.id}`}>
            <div className="aspect-[4/3] bg-linen">
              {m.kind === "video"
                ? <video src={resolveImg(m.url)} className="w-full h-full object-cover" muted />
                : <img src={resolveImg(m.url)} alt={m.original_filename} className="w-full h-full object-cover" loading="lazy" />}
            </div>
            <div className="p-3">
              <div className="text-xs truncate text-espressosoft">{m.original_filename}</div>
              <div className="flex gap-2 mt-2">
                <button onClick={() => copy(m.url)} className="flex-1 flex items-center justify-center gap-1 text-[0.65rem] uppercase tracking-wide border border-espresso/15 rounded py-1.5 hover:bg-linen" data-testid={`media-copy-${m.id}`}><Copy size={12} /> Copy</button>
                <button onClick={() => remove(m.id)} className="text-destructive/60 hover:text-destructive px-2" data-testid={`media-delete-${m.id}`}><Trash2 size={14} /></button>
              </div>
            </div>
          </div>
        ))}
      </div>
      {items.length === 0 && !uploading && <p className="text-taupe mt-4">Your media library is empty.</p>}
    </AdminShell>
  );
}
